import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/lib/me';
import { AccessDenied, errorStatus } from '@/components/RouteError';
import { useToast } from '@/components/ToastProvider';
import { SubmissionForm } from '@/components/finance/SubmissionForm';
import { errorMessage } from '@/lib/errors';
import { financeSubmissionRefsQuery } from '@/lib/finance';

// The phone submission form (spec §8.3) — filled in the field, on a phone,
// by a finance_submitter actor who has NO general finance.read (rulings.md
// #32). Gated on finance.submit; financeSubmissionRefsQuery is the ONLY
// data fetch this route makes — it is the one reference endpoint written
// for exactly this actor (getFinanceSubmissionRefs, server-side gated on
// finance.submit too). Reaching for financeCategoriesQuery/
// financeAccountsQuery/apartmentsQuery instead — the shape every OTHER
// finance screen in this app uses for the same reference data — would 403
// a submitter-only actor on the one finance.* screen they are supposed to
// reach at all.
export const Route = createFileRoute('/_app/finance/submit')({ component: SubmitPage });

function SubmitPage() {
  return <SubmitScreen />;
}

export function SubmitScreen() {
  const { can } = usePermissions();
  const { toast } = useToast();
  const navigate = useNavigate();
  const refs = useQuery(financeSubmissionRefsQuery);

  if (!can('finance.submit') || errorStatus(refs.error) === 403) return <AccessDenied />;
  if (refs.isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (refs.isError || !refs.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(refs.error)}
      </p>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      <SubmissionForm
        refs={refs.data}
        onDone={(submission) => {
          toast({
            kind: 'success',
            title: submission.status === 'draft' ? 'Draft saved' : 'Submission sent',
          });
          // Only a real Send moves the operator on — Save draft (status
          // stays 'draft') keeps them on the form, e.g. to attach another
          // photo before finishing (spec §14's own sequence: create →
          // upload receipts → send).
          if (submission.status !== 'draft') {
            void navigate({ to: '/finance/submissions' });
          }
        }}
      />
    </div>
  );
}
